import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  InputBase,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { COLORS, FONT_FAMILY, TYPOGRAPHY } from "../../constants/color";
import { Icon } from "../../icons";
import { useAuth } from "../../context/authContext";
import axios from "axios";
import { url } from "../../constants/url";

// Credit status styles
const creditStatusStyles = {
  ADDED: { bgcolor: "#8CD47E", color: "#fff" },
  USED: { bgcolor: "#FF6961", color: "#fff" },
  REFUNDED: { bgcolor: "#FFB54C", color: "#fff" },
  EXPIRED: { bgcolor: "#777777", color: "#fff" },
};

const usageTypeStyles = {
  CREDIT_PURCHASE: { bgcolor: "#4CAF50", color: "#fff" },
  CHECKLIST_REQUEST: { bgcolor: "#2196F3", color: "#fff" },
  MANUAL_ADJUSTMENT: { bgcolor: "#9C27B0", color: "#fff" },
  REFUND: { bgcolor: "#FF9800", color: "#fff" },
};

const FIRCreditHistory = () => {
  const { isAuthenticated, hasRole, token } = useAuth();
  const [creditData, setCreditData] = useState([]);
  const [creditPlans, setCreditPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize] = useState(12);

  // Modal state
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientHistoryModalOpen, setClientHistoryModalOpen] = useState(false);
  const [clientHistory, setClientHistory] = useState([]);
  const [clientDetails, setClientDetails] = useState(null);
  const [clientCreditSummary, setClientCreditSummary] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchDebounce, setSearchDebounce] = useState("");

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch credit plans
  const fetchAllCreditPlans = useCallback(async () => {
    try {
      const response = await axios.get(`${url}/api/credits/fetch`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: 1, limit: 100 },
      });
      const plansMap = {};
      response.data.plans.forEach((plan) => {
        plansMap[plan._id] = {
          planName: plan.planName,
          credits: plan.credits,
          amount: plan.amount,
        };
      });
      setCreditPlans(plansMap);
    } catch (err) {
      console.error("Error fetching credit plans:", err);
    }
  }, [token]);

  // Fetch only Individual clients credit data
  const fetchIndividualClientsCreditData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: page,
        limit: pageSize,
        role: "CLIENT",
        roleType: "Individual",
      };

      if (searchDebounce.trim()) {
        params.search = searchDebounce.trim();
      }

      const response = await axios.get(`${url}/api/credits/data/all`, {
        headers: { Authorization: `Bearer ${token}` },
        params: params,
      });

      const { data, totalPages: tp, total } = response.data;
      setCreditData(data);
      setTotalRecords(total);
      setTotalPages(tp);
    } catch (err) {
      console.error("Error fetching FIR credit data:", err);
      setError(err.response?.data?.message || "Failed to load credit data");
      setCreditData([]);
    } finally {
      setLoading(false);
    }
  }, [token, page, pageSize, searchDebounce]);

  useEffect(() => {
    if (!isAuthenticated || !token) {
      setLoading(false);
      return;
    }
    fetchAllCreditPlans();
    fetchIndividualClientsCreditData();
  }, [
    isAuthenticated,
    token,
    fetchAllCreditPlans,
    fetchIndividualClientsCreditData,
  ]);

  // Fetch detailed client history for modal
  const fetchClientDetailedHistory = async (clientId, clientData) => {
    try {
      setHistoryLoading(true);

      const response = await axios.get(`${url}/api/credits/data/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { historyPage: 1, historyLimit: 100 },
      });

      const { creditHistory, creditSummary, purchasedFirPlans } = response.data;
      // Individual clients: filter out OUTSTATION records and template/usage data (i.e. only show CREDIT_PURCHASE or empty usageType)
      const records = (creditHistory.records || []).filter(
        (r) =>
          r.planType !== "OUTSTATION" &&
          (r.usageType === "CREDIT_PURCHASE" || !r.usageType),
      );
      setClientHistory(records);
      setClientCreditSummary(creditSummary);
      setClientDetails({
        name: clientData.name,
        email: clientData.email,
        phone: clientData.phone,
        purchasedFirPlans: purchasedFirPlans || [],
      });
      setClientHistoryModalOpen(true);
    } catch (err) {
      console.error("Error fetching client detailed history:", err);
      alert(err.response?.data?.message || "Failed to load client history");
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleViewClientHistory = (client) => {
    setSelectedClient(client);
    fetchClientDetailedHistory(client.userId, client);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const clearSearch = () => {
    setSearchQuery("");
    setPage(1);
  };

  const startIndex = totalRecords > 0 ? (page - 1) * pageSize + 1 : 0;
  const endIndex = Math.min(page * pageSize, totalRecords);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = String(date.getFullYear()).slice(-2);
      return `${day}/${month}/${year}`;
    } catch {
      return dateString;
    }
  };

  const formatAmount = (amount) => {
    if (amount === null || amount === undefined) return "N/A";
    if (amount === 0) return "₹0";
    return `₹${amount.toLocaleString("en-IN")}`;
  };

  const getDisplayName = (record) => {
    if (record.usageType === "CHECKLIST_REQUEST")
      return record.templateTitle || record.requestName || "Project";
    if (record.planName === "Other Plan" && record.otherPlan)
      return `Custom Plan (${record.otherPlan.credits} credits)`;
    return record.planName || "Credit Plan";
  };

  const getDisplayAmount = (record) => {
    if (record.planName === "Other Plan" && record.otherPlan)
      return record.otherPlan.amount || 0;
    if (record.creditID && creditPlans[record.creditID])
      return creditPlans[record.creditID].amount;
    if (record.amount !== undefined) return record.amount;
    return null;
  };

  const getUsageTypeDisplay = (record) => {
    if (record.usageType === "CHECKLIST_REQUEST") return "PROJECT";
    if (record.usageType === "CREDIT_PURCHASE") return "PURCHASE";
    return record.usageType || "OTHER";
  };

  const tableHeaders = [
    { key: "Client Info", width: "200px" },
    { key: "History", width: "100px" },
    { key: "Status", width: "100px" },
    { key: "View", width: "80px" },
  ];

  if (!isAuthenticated || !(hasRole("ADMIN") || hasRole("SUPERADMIN"))) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6" color="error">
          This page is only accessible to ADMIN users.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography
            sx={{
              ...TYPOGRAPHY.H2,
              fontFamily: FONT_FAMILY,
              color: COLORS.PRIMARY,
            }}
          >
            Individual Clients Plan History
          </Typography>
          <Typography
            sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.MUTED_TEXT, mt: 0.5 }}
          >
            Credit history for clients with Individual role type
          </Typography>
        </Box>

        {/* Search Bar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexGrow: 0,
            width: { xs: 180, sm: 250, md: 280 },
            px: 2,
            py: 0.7,
            borderRadius: 1.5,
            border: `1px solid ${COLORS.PRIMARY}`,
            backgroundColor: COLORS.BACKGROUND,
            color: COLORS.PRIMARY,
          }}
        >
          <Icon name="search" sx={{ fontSize: 18, color: "currentColor" }} />
          <InputBase
            placeholder="Search clients..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            sx={{
              flex: 1,
              fontFamily: FONT_FAMILY,
              ...TYPOGRAPHY.BODY_STANDARD,
              color: COLORS.TEXT,
              ml: 1,
              mt: 0.5,
            }}
          />
          {searchQuery && (
            <IconButton
              size="small"
              onClick={clearSearch}
              sx={{ padding: 0.5 }}
            >
              <Icon name="close2" sx={{ fontSize: 14 }} color={COLORS.ERROR} />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <Box
        sx={{
          width: "100%",
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
          "&::-webkit-scrollbar": { height: "6px" },
          "&::-webkit-scrollbar-track": { backgroundColor: "#f1f1f1" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: COLORS.LIGHT_SECONDARY,
            borderRadius: "4px",
          },
        }}
      >
        <Paper
          elevation={0}
          sx={{ backgroundColor: "transparent", minWidth: "880px" }}
        >
          <TableContainer>
            <Table
              sx={{
                backgroundColor: "transparent",
                "& thead th": {
                  backgroundColor: "transparent",
                  padding: "8px 6px",
                },
                "& tbody td": {
                  backgroundColor: "transparent",
                  borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                  padding: "8px 6px",
                },
              }}
            >
              <TableHead>
                <TableRow>
                  {tableHeaders.map((header) => (
                    <TableCell
                      key={header.key}
                      sx={{
                        width: header.width,
                        fontFamily: FONT_FAMILY,
                        fontWeight: 700,
                        color: COLORS.PRIMARY,
                        fontSize: "0.85rem",
                      }}
                    >
                      {header.key}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={tableHeaders.length}
                      align="center"
                      sx={{ py: 5 }}
                    >
                      <CircularProgress
                        size={36}
                        sx={{ color: COLORS.PRIMARY }}
                      />
                    </TableCell>
                  </TableRow>
                ) : creditData.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={tableHeaders.length}
                      align="center"
                      sx={{ py: 5 }}
                    >
                      <Typography
                        sx={{
                          color: COLORS.MUTED_TEXT,
                          fontFamily: FONT_FAMILY,
                        }}
                      >
                        No Individual clients found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  creditData.map((client, index) => (
                    <TableRow
                      key={client.userId || index}
                      sx={{
                        "&:hover": { backgroundColor: "rgba(0,0,0,0.02)" },
                      }}
                    >
                      <TableCell>
                        <Typography
                          sx={{
                            fontFamily: FONT_FAMILY,
                            fontWeight: 600,
                            fontSize: "0.85rem",
                            color: COLORS.TEXT,
                          }}
                        >
                          {client.name}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: FONT_FAMILY,
                            fontSize: "0.75rem",
                            color: COLORS.MUTED_TEXT,
                          }}
                        >
                          {client.email}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: FONT_FAMILY,
                            fontSize: "0.72rem",
                            color: COLORS.MUTED_TEXT,
                          }}
                        >
                          {client.phone}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${client.creditHistoryCount || 0} records`}
                          size="small"
                          sx={{
                            bgcolor: COLORS.LIGHT_SECONDARY,
                            color: COLORS.PRIMARY,
                            fontFamily: FONT_FAMILY,
                            fontSize: "0.72rem",
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={client.isApproved ? "Approved" : "Pending"}
                          size="small"
                          sx={{
                            bgcolor: client.isApproved ? "#8CD47E" : "#FFB54C",
                            color: "#fff",
                            fontFamily: FONT_FAMILY,
                            fontSize: "0.72rem",
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleViewClientHistory(client)}
                          disabled={
                            historyLoading &&
                            selectedClient?.userId === client.userId
                          }
                          sx={{
                            fontFamily: FONT_FAMILY,
                            fontSize: "0.72rem",
                            color: COLORS.PRIMARY,
                            borderColor: COLORS.PRIMARY,
                            textTransform: "none",
                            px: 1.5,
                            "&:hover": {
                              backgroundColor: COLORS.PRIMARY,
                              color: "#fff",
                            },
                          }}
                        >
                          VIEW
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      {/* Pagination */}
      {!loading && creditData.length > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 2,
            p: 2,
            mt: 2,
          }}
        >
          <Typography
            sx={{ fontSize: 13, color: "#777", whiteSpace: "nowrap" }}
          >
            {startIndex} - {endIndex} of {totalRecords} records
          </Typography>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            <IconButton
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
              size="small"
              sx={{ padding: "4px" }}
            >
              <Icon name="chevronLeft" size={16} />
            </IconButton>
            {Array.from({ length: Math.min(3, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 3) pageNum = i + 1;
              else if (page === 1) pageNum = i + 1;
              else if (page === totalPages) pageNum = totalPages - 2 + i;
              else pageNum = page - 1 + i;
              if (pageNum > 0 && pageNum <= totalPages) {
                return (
                  <Button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    sx={{
                      minWidth: 24,
                      height: 24,
                      fontSize: "0.7rem",
                      bgcolor:
                        page === pageNum ? COLORS.PRIMARY : "transparent",
                      color: page === pageNum ? "#fff" : COLORS.TEXT,
                      border: page === pageNum ? "none" : "1px solid #E1BEE7",
                      "&:hover": {
                        bgcolor:
                          page === pageNum
                            ? COLORS.PRIMARY
                            : "rgba(0,0,0,0.04)",
                      },
                    }}
                  >
                    {pageNum}
                  </Button>
                );
              }
              return null;
            })}
            <IconButton
              onClick={() => handlePageChange(page + 1)}
              disabled={page === totalPages}
              size="small"
              sx={{ padding: "4px" }}
            >
              <Icon name="chevronRight" size={16} />
            </IconButton>
          </Box>
        </Box>
      )}

      {/* Client History Modal */}
      <Dialog
        open={clientHistoryModalOpen}
        onClose={() => setClientHistoryModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, maxHeight: "80vh" } }}
      >
        <DialogTitle
          sx={{
            fontFamily: FONT_FAMILY,
            color: COLORS.PRIMARY,
            fontWeight: 700,
            pb: 1,
          }}
        >
          Credit History — {clientDetails?.name}
          <Typography
            sx={{
              fontFamily: FONT_FAMILY,
              fontSize: "0.8rem",
              color: COLORS.MUTED_TEXT,
            }}
          >
            {clientDetails?.email} • {clientDetails?.phone}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ p: 2 }}>
          {historyLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
              <CircularProgress size={32} sx={{ color: COLORS.PRIMARY }} />
            </Box>
          ) : clientHistory.length === 0 ? (
            <Typography
              sx={{
                fontFamily: FONT_FAMILY,
                color: COLORS.MUTED_TEXT,
                textAlign: "center",
                py: 4,
              }}
            >
              No credit history available
            </Typography>
          ) : (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{ border: `1px solid ${COLORS.BORDER}`, borderRadius: 2 }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f9f9f9" }}>
                    {["Date", "Plan Name", "Plan Used", "Amount", "Action"].map((h) => (
                      <TableCell
                        key={h}
                        sx={{
                          fontFamily: FONT_FAMILY,
                          fontWeight: 700,
                          fontSize: "0.78rem",
                          color: COLORS.PRIMARY,
                          padding: "8px 10px",
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clientHistory.map((record, index) => {
                    const displayName = getDisplayName(record);
                    const displayAmount = getDisplayAmount(record);

                    const usedStatus = (() => {
                      if (!clientDetails?.purchasedFirPlans) return "No";
                      const match = clientDetails.purchasedFirPlans.find(
                        (plan) => {
                          const recordTime = new Date(
                            record.dateAdded,
                          ).getTime();
                          const planTime = new Date(plan.purchasedAt).getTime();
                          return (
                            Math.abs(recordTime - planTime) < 5000 &&
                            plan.price === displayAmount
                          );
                        },
                      );
                      return match?.isConsumed ? "Yes" : "No";
                    })();

                    return (
                      <TableRow
                        key={index}
                        sx={{
                          borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                        }}
                      >
                        <TableCell sx={{ padding: "8px 10px" }}>
                          <Typography
                            sx={{
                              fontFamily: FONT_FAMILY,
                              fontSize: "0.78rem",
                              color: COLORS.TEXT,
                            }}
                          >
                            {formatDate(record.dateAdded)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ padding: "8px 10px" }}>
                          <Typography
                            sx={{
                              fontFamily: FONT_FAMILY,
                              fontSize: "0.78rem",
                              color: COLORS.TEXT,
                            }}
                          >
                            {displayName}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ padding: "8px 10px" }}>
                          <Chip
                            label={usedStatus}
                            size="small"
                            sx={{
                              bgcolor:
                                usedStatus === "Yes"
                                  ? "#8CD47E"
                                  : COLORS.LIGHT_SECONDARY,
                              color: usedStatus === "Yes" ? "#fff" : "#000",
                              fontWeight: 600,
                              fontSize: "0.75rem",
                              height: 20,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ padding: "8px 10px" }}>
                          <Typography
                            sx={{
                              fontFamily: FONT_FAMILY,
                              fontSize: "0.78rem",
                              color: COLORS.PRIMARY,
                              fontWeight: 700,
                            }}
                          >
                            {displayAmount !== null
                              ? formatAmount(displayAmount)
                              : "—"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ padding: "8px 10px" }}>
                          <IconButton
                            size="small"
                            title="Download Invoice"
                            onClick={() => window.open(`${url}/api/invoice/download/${selectedClient?.userId || selectedClient?._id}/${record._id}?token=${token}`)}
                            sx={{ color: COLORS.PRIMARY }}
                          >
                            <Icon name="download" size={16} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setClientHistoryModalOpen(false)}
            sx={{
              fontFamily: FONT_FAMILY,
              color: COLORS.PRIMARY,
              borderColor: COLORS.PRIMARY,
              textTransform: "none",
            }}
            variant="outlined"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FIRCreditHistory;
